/**
 * ⚠️ FORKED COMPONENT — DO NOT MERGE BACK ⚠️
 *
 * FORKED FROM: src/components/search/SearchResultsTable.tsx
 *
 * LR DIRECT VERSION
 * This file is intentionally forked to keep LR Direct fully isolated from the main Search Loads system.
 *
 * RULES:
 * - Do NOT import or reuse logic from Search Loads
 * - Do NOT connect to `loads` table
 * - Only use `direct_loads`, `direct_load_stops`, `direct_bids`
 * - Changes here must NOT affect Search Loads
 *
 * If you need shared behavior, duplicate — do not abstract.
 */
import React, { useState, useMemo } from "react";
import { PackageOpen, Search, ChevronDown, ChevronUp, Check, Clock, Lock, X } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { format } from "date-fns";
import { DirectLoadDetailPanel } from "./DirectLoadDetailPanel";
import { formatEquipmentType } from "@/lib/equipmentTypes";
import { PrivateNetworkBadge } from "./PrivateNetworkBadge";
import { LtlBadge, isLtlLoad } from "./LtlBadge";
import { isBiddingOpen } from "@/lib/lrDirect/directLoadStatus";
import { useDirectLoadStopCounts, formatStopCounts } from "@/hooks/lrDirect/useDirectLoadStopCounts";


/* ─── DirectLoad type ────────────────────────────────────────────── */

export interface DirectLoad {
  id: string;
  origin_city: string;
  origin_state: string;
  destination_city: string;
  destination_state: string;
  equipment_type: string;
  pickup_date: string;
  delivery_date: string | null;
  commodity: string | null;
  weight_lbs: number | null;
  bid_deadline: string | null;
  max_bidders: number;
  priority_tag: string;
  total_miles: number | null;
  total_miles_source?: string | null;
  load_type: string | null;
  load_nature?: string | null;
  posted_at: string | null;
  length_ft?: number | null;
  target_rate_min?: number | null;
  target_rate_max?: number | null;
  // Phase 1: surfaced from DB
  shipper_notes_to_brokers?: string | null;
  special_instructions?: string | null;
  reference_number?: string | null;
  // Phase 3: new columns
  appointment_type?: string | null;
  handling_requirements?: string[] | null;
  // Phase 1D: private network visibility
  visibility_mode?: "open_market" | "private_only" | "both" | null;
  private_network_id?: string | null;
  post_type?: "load" | "quote" | null;
  /** Needed by isBiddingOpen() — must be selected by every board query. */
  status?: string | null;
  // Optional voice Load Brief (LR Direct isolated storage path)
  brief_audio_path?: string | null;
  brief_duration_seconds?: number | null;
  /** Cached ATLAS load analysis, generated once at post time. */
  atlas_analysis?: unknown;
}

/* ─── Sort types (subset for LR Direct) ──────────────────────────── */

export type DirectSortOption =
  | 'age_newest' | 'age_oldest'
  | 'pickup_soonest' | 'pickup_latest'
  | 'trip_shortest' | 'trip_longest'
  | 'weight_heaviest' | 'weight_lightest'
  | 'origin_az' | 'origin_za'
  | 'dest_az' | 'dest_za'
  | 'eq_az' | 'eq_za';

interface SortDef { label: string; shortLabel: string; }

const SORT_GROUPS: { heading: string; options: { key: DirectSortOption; def: SortDef }[] }[] = [
  {
    heading: "Recommended",
    options: [
      { key: "age_newest", def: { label: "Age — Newest", shortLabel: "Age — Newest" } },
      { key: "pickup_soonest", def: { label: "Pickup — Soonest", shortLabel: "Pickup — Soonest" } },
    ],
  },
  {
    heading: "Distance",
    options: [
      { key: "trip_shortest", def: { label: "Trip — Shortest", shortLabel: "Trip — Shortest" } },
      { key: "trip_longest", def: { label: "Trip — Longest", shortLabel: "Trip — Longest" } },
    ],
  },
  {
    heading: "Load Details",
    options: [
      { key: "weight_heaviest", def: { label: "Weight — Heaviest", shortLabel: "Weight — Heaviest" } },
      { key: "weight_lightest", def: { label: "Weight — Lightest", shortLabel: "Weight — Lightest" } },
      { key: "eq_az", def: { label: "Equipment — A→Z", shortLabel: "EQ — A→Z" } },
      { key: "eq_za", def: { label: "Equipment — Z→A", shortLabel: "EQ — Z→A" } },
      { key: "origin_az", def: { label: "Origin — A→Z", shortLabel: "Origin — A→Z" } },
      { key: "dest_az", def: { label: "Dest — A→Z", shortLabel: "Dest — A→Z" } },
    ],
  },
];

const SORT_LABEL_MAP: Record<string, string> = {};
for (const g of SORT_GROUPS) for (const o of g.options) SORT_LABEL_MAP[o.key] = o.def.shortLabel;

/* ─── Age helpers ────────────────────────────────────────────────── */

function formatAge(postedAt: string): { text: string; mins: number } {
  const mins = Math.floor((Date.now() - new Date(postedAt).getTime()) / 60000);
  if (mins < 60) return { text: `${mins}m ago`, mins };
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return { text: m > 0 ? `${h}h ${m}m` : `${h}h ago`, mins };
}

function getAgeText(mins: number): string {
  if (mins < 60) return "text-emerald-600/50";
  if (mins < 120) return "text-amber-600/50";
  if (mins < 1440) return "text-orange-600/50";
  return "text-red-600/50";
}

/* ─── Equipment map ──────────────────────────────────────────────── */

const eqMap: Record<string, string> = {
  van: "V", reefer: "R", flatbed: "F",
  step_deck: "SD", power_only: "PO",
  box_truck: "BT", hotshot: "HS", other: "O",
};

/* ─── Weight formatter ────────────────────────────────────────────── */

function formatWeight(lbs: number | null | undefined): string {
  if (!lbs) return "—";
  if (lbs >= 1000) return `${Math.round(lbs / 1000)}k lbs`;
  return `${lbs} lbs`;
}

/* ─── Size badge (display-only derivation from load_type) ─────────── */

function sizeLabel(loadType: string | null | undefined): string {
  if (!loadType) return "—";
  const v = loadType.trim().toLowerCase();
  if (v === "ftl" || v === "full") return "FTL";
  if (v === "ltl" || v === "partial") return "LTL";
  if (v === "other") return "OTHER";
  return loadType;
}

/* ─── Nature label (spot / dedicated / contract) ─────────────────── */
function natureLabel(nature: string | null | undefined): string | null {
  const v = (nature ?? "").trim().toLowerCase();
  if (!v) return null;
  if (v === "spot") return "SPOT";
  if (v === "dedicated") return "DEDICATED";
  if (v === "contract") return "CONTRACT";
  return nature!.toUpperCase();
}

/* ─── Chip color tokens (capacity + nature) ─────────────────────── */
const CHIP_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  FTL: { bg: "var(--chip-ftl-bg)", text: "var(--chip-ftl-text)", border: "var(--chip-ftl-border)" },
  LTL: { bg: "var(--chip-ltl-bg)", text: "var(--chip-ltl-text)", border: "var(--chip-ltl-border)" },
  OTHER: { bg: "var(--chip-other-bg)", text: "var(--chip-other-text)", border: "var(--chip-other-border)" },
  SPOT: { bg: "var(--chip-spot-bg)", text: "var(--chip-spot-text)", border: "var(--chip-spot-border)" },
  DEDICATED: { bg: "var(--chip-dedicated-bg)", text: "var(--chip-dedicated-text)", border: "var(--chip-dedicated-border)" },
  CONTRACT: { bg: "var(--chip-contract-bg)", text: "var(--chip-contract-text)", border: "var(--chip-contract-border)" },
};

function chipStyle(label: string) {
  return CHIP_STYLES[label] ?? CHIP_STYLES.OTHER;
}

function CapacityBadge({ loadType, className }: { loadType: string | null | undefined; className?: string }) {
  const label = sizeLabel(loadType);
  const style = chipStyle(label);
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-sm px-1 h-4 text-[9px] font-bold uppercase tracking-wide border",
        className
      )}
      style={{
        backgroundColor: `hsl(${style.bg})`,
        color: `hsl(${style.text})`,
        borderColor: `hsl(${style.border})`,
      }}
    >
      {label}
    </span>
  );
}

function NatureChip({ nature, className }: { nature: string | null | undefined; className?: string }) {
  const label = natureLabel(nature);
  if (!label) return null;
  const style = chipStyle(label);
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-sm px-1 h-4 text-[9px] font-bold uppercase tracking-wide border",
        className
      )}
      style={{
        backgroundColor: `hsl(${style.bg})`,
        color: `hsl(${style.text})`,
        borderColor: `hsl(${style.border})`,
      }}
    >
      {label}
    </span>
  );
}

function formatLength(ft: number | null | undefined): string {
  if (!ft) return "";
  return `${ft}'`;
}


/* ─── Header sort mapping ────────────────────────────────────────── */

type SortPair = [DirectSortOption, DirectSortOption];

const HEADER_SORT_MAP: Record<string, SortPair> = {
  age: ["age_newest", "age_oldest"],
  origin: ["origin_az", "origin_za"],
  dest: ["dest_az", "dest_za"],
  pickup: ["pickup_soonest", "pickup_latest"],
  weight: ["weight_heaviest", "weight_lightest"],
  eq: ["eq_az", "eq_za"],
  trip: ["trip_shortest", "trip_longest"],
};

/* ─── Column header shorthand ────────────────────────────────────── */

const TH = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <TableHead className={cn("px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground whitespace-nowrap", className)}>
    {children}
  </TableHead>
);

interface SortableTHProps {
  children: React.ReactNode;
  className?: string;
  sortKey: string;
  currentSort: DirectSortOption;
  onSort: (sort: DirectSortOption) => void;
}

const SortableTH = ({ children, className, sortKey, currentSort, onSort }: SortableTHProps) => {
  const pair = HEADER_SORT_MAP[sortKey];
  if (!pair) return <TH className={className}>{children}</TH>;

  const [primary, reverse] = pair;
  const isActive = currentSort === primary || currentSort === reverse;
  const isReversed = currentSort === reverse;

  const handleClick = () => {
    if (!isActive) onSort(primary);
    else onSort(isReversed ? primary : reverse);
  };

  return (
    <TableHead
      className={cn(
        "px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider whitespace-nowrap cursor-pointer select-none transition-colors",
        isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground/70",
        className
      )}
      onClick={handleClick}
    >
      <span className="inline-flex items-center gap-0.5">
        {children}
        {isActive && (isReversed ? <ChevronUp className="h-2.5 w-2.5" /> : <ChevronDown className="h-2.5 w-2.5" />)}
      </span>
    </TableHead>
  );
};

/* ─── Props ──────────────────────────────────────────────────────── */

interface DirectResultsTableProps {
  loads: DirectLoad[];
  isLoading?: boolean;
  sortBy?: DirectSortOption;
  onSortChange?: (sort: DirectSortOption) => void;
  hasActiveFilters?: boolean;
  hasAnyLoads?: boolean;
  onClearFilters?: () => void;
}

/* ═══════════════════════════════════════════════════════════════════ */
export function DirectResultsTable({
  loads,
  isLoading,
  sortBy = "age_newest",
  onSortChange,
  hasActiveFilters,
  hasAnyLoads,
  onClearFilters,
}: DirectResultsTableProps) {
  const [sortOpen, setSortOpen] = useState(false);
  const [expandedLoadId, setExpandedLoadId] = useState<string | null>(null);
  const showSkeleton = isLoading && loads.length === 0;

  /* ── Stop counts (display-only, batched fetch on direct_load_stops) ── */
  const stopCounts = useDirectLoadStopCounts(useMemo(() => loads.map((l) => l.id), [loads]));


  /* ── Sorting ────────────────────────────────────────────────── */
  const sortedLoads = useMemo(() => {
    const arr = [...loads];
    const cmp = (a: DirectLoad, b: DirectLoad): number => {
      switch (sortBy) {
        case "age_newest":
          return new Date(b.posted_at || "").getTime() - new Date(a.posted_at || "").getTime();
        case "age_oldest":
          return new Date(a.posted_at || "").getTime() - new Date(b.posted_at || "").getTime();
        case "pickup_soonest":
          return (a.pickup_date || "9999").localeCompare(b.pickup_date || "9999");
        case "pickup_latest":
          return (b.pickup_date || "0000").localeCompare(a.pickup_date || "0000");
        case "trip_shortest":
          return (a.total_miles ?? Infinity) - (b.total_miles ?? Infinity);
        case "trip_longest":
          return (b.total_miles ?? 0) - (a.total_miles ?? 0);
        case "weight_heaviest":
          return (b.weight_lbs ?? 0) - (a.weight_lbs ?? 0);
        case "weight_lightest":
          return (a.weight_lbs ?? 0) - (b.weight_lbs ?? 0);
        case "origin_az":
          return (a.origin_city || "").localeCompare(b.origin_city || "");
        case "origin_za":
          return (b.origin_city || "").localeCompare(a.origin_city || "");
        case "dest_az":
          return (a.destination_city || "").localeCompare(b.destination_city || "");
        case "dest_za":
          return (b.destination_city || "").localeCompare(a.destination_city || "");
        case "eq_az":
          return (a.equipment_type || "").localeCompare(b.equipment_type || "");
        case "eq_za":
          return (b.equipment_type || "").localeCompare(a.equipment_type || "");
        default:
          return 0;
      }
    };
    arr.sort(cmp);
    return arr;
  }, [loads, sortBy]);

  /* ── Empty state ─────────────────────────────────────────────── */
  if (!isLoading && loads.length === 0) {
    const filterCase = hasActiveFilters && (hasAnyLoads ?? false);
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 text-center gap-3">
        {filterCase ? (
          <Search className="h-8 w-8 opacity-30 text-muted-foreground" />
        ) : (
          <PackageOpen className="h-8 w-8 opacity-30 text-muted-foreground" />
        )}
        <div className="flex flex-col gap-1 max-w-md">
          <span className="text-sm font-medium text-foreground">
            {filterCase ? "No loads match your filters" : "No freight posted right now"}
          </span>
          <span className="text-xs text-muted-foreground">
            {filterCase
              ? "Try adjusting or clearing your filters to see more loads."
              : "New loads are coming — you'll be notified when loads matching your lanes are posted."}
          </span>
        </div>
        {filterCase && onClearFilters && (
          <Button onClick={onClearFilters} variant="outline" size="sm" className="gap-1 mt-1">
            <X className="h-3.5 w-3.5" /> Clear filters
          </Button>
        )}
      </div>
    );
  }

  const COL_SPAN = 12;

  return (
    <div className="data-table rounded-lg border border-border bg-card overflow-hidden" style={{ boxShadow: "var(--shadow-sm)" }}>
      <style>{`
        @keyframes indeterminate {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(0%); }
          100% { transform: translateX(100%); }
        }
        .col-sep { position: relative; }
        .col-sep::after {
          content: '';
          position: absolute;
          right: 0;
          top: 20%;
          height: 60%;
          width: 1px;
          background: hsl(var(--border));
          opacity: 0.8;
        }
      `}</style>
      {/* Toolbar */}
      <div className="px-3 py-px border-b bg-muted/20 flex items-center gap-3">
        <span className="text-xs font-semibold text-muted-foreground">
          {showSkeleton ? "Searching…" : `${sortedLoads.length} Results`}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Sort:</span>
          <Popover open={sortOpen} onOpenChange={setSortOpen}>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-1.5 text-xs font-medium text-foreground bg-muted/40 hover:bg-muted border border-border rounded-md px-2.5 py-1 transition-colors">
                {SORT_LABEL_MAP[sortBy] ?? "Age — Newest"}
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-0" align="start" sideOffset={4}>
              <Command>
                <CommandList className="max-h-[360px]">
                  {SORT_GROUPS.map((group) => (
                    <CommandGroup key={group.heading} heading={group.heading}>
                      {group.options.map(({ key, def }) => (
                        <CommandItem
                          key={key}
                          value={key}
                          onSelect={() => {
                            onSortChange?.(key);
                            setSortOpen(false);
                          }}
                          className="text-xs cursor-pointer"
                        >
                          <Check className={cn("mr-2 h-3 w-3", sortBy === key ? "opacity-100" : "opacity-0")} />
                          {def.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Progress bar */}
      {isLoading && (
        <div className="h-0.5 w-full overflow-hidden">
          <Progress value={75} className="h-0.5 rounded-none [&>div]:animate-[indeterminate_1.5s_ease-in-out_infinite]" />
        </div>
      )}
      {!isLoading && <div className="h-0.5" />}

      {/* ── Mobile card list (presentation only — same expand handler) ── */}
      <div className="md:hidden flex flex-col gap-2 p-3">
        {showSkeleton && Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={`mskel-${i}`} className="h-16 w-full rounded-lg" />
        ))}
        {!showSkeleton && sortedLoads.map((load) => {
          const age = formatAge(load.posted_at || "");
          const equipLabel = load.equipment_type ? formatEquipmentType(load.equipment_type) : "—";
          const lengthLabel = formatLength(load.length_ft);
          const stopsLabel = formatStopCounts(stopCounts[load.id]);
          const isExpanded = expandedLoadId === load.id;

          return (
            <React.Fragment key={`m-${load.id}`}>
              <button
                type="button"
                onClick={() => setExpandedLoadId(isExpanded ? null : load.id)}
                className={cn(
                  "text-left rounded-lg border border-border bg-card p-3 min-w-0",
                  "active:bg-muted focus:outline-none focus:ring-2 focus:ring-brand-teal-deep",
                  load.priority_tag === "priority" && isBiddingOpen(load) && "border-l-[3px] border-l-destructive",
                  isLtlLoad(load.load_type) && "border-l-[4px] border-l-amber-500 bg-amber-50/70",
                  isExpanded && "border-l-2 border-l-brand-teal bg-brand-teal/10",
                )}
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {isLtlLoad(load.load_type) && <LtlBadge size="default" label="LTL / Partial" />}
                    {load.post_type === "quote" && (
                      <Badge className="text-[9px] px-1.5 py-0 h-4 font-bold bg-amber-500 hover:bg-amber-500 text-white border-0 shrink-0">
                        QUOTE
                      </Badge>
                    )}
                    <span className="text-sm font-semibold truncate min-w-0">
                      {load.origin_city}, {load.origin_state} → {load.destination_city}, {load.destination_state}
                    </span>
                  </div>
                  <span className={cn("text-[10px] font-semibold whitespace-nowrap shrink-0", getAgeText(age.mins))}>
                    {age.text}
                  </span>
                </div>
                {load.visibility_mode === "private_only" && (
                  <div className="mb-1.5">
                    <PrivateNetworkBadge networkId={load.private_network_id} variant="card" />
                  </div>
                )}
                <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span className="truncate min-w-0">
                    {equipLabel}
                    {lengthLabel && <> {lengthLabel}</>}
                    {" · "}
                    <CapacityBadge loadType={load.load_type} />
                    {natureLabel(load.load_nature) && <> · <NatureChip nature={load.load_nature} /></>}
                    {" · "}
                    <span className="font-mono font-semibold text-foreground">{stopsLabel}</span>
                    {load.pickup_date && <> · {format(new Date(load.pickup_date + "T00:00:00"), "MMM d")}</>}
                    {load.total_miles ? <> · {Number(load.total_miles).toLocaleString()}mi</> : null}
                  </span>

                </div>
                {load.bid_deadline && (
                  <div className="mt-1 flex items-center gap-1 text-[11px] text-amber-600">
                    <Clock className="h-3 w-3" />
                    <span>Bid by {format(new Date(load.bid_deadline), "MMM d, h:mm a")}</span>
                  </div>
                )}
              </button>
              {isExpanded && (
                <div className="rounded-lg border border-border bg-card overflow-hidden">
                  <DirectLoadDetailPanel load={load} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div className="hidden md:block">
        <Table className="table-fixed w-full">
          <TableHeader className="sticky top-0 z-20">
            <TableRow className="bg-muted/40 hover:bg-muted/40 border-b border-border">
              <SortableTH className="w-[150px] px-1.5" sortKey="age" currentSort={sortBy} onSort={onSortChange!}>Age</SortableTH>
              <SortableTH className="w-[110px] col-sep px-1.5" sortKey="origin" currentSort={sortBy} onSort={onSortChange!}>Origin</SortableTH>
              <SortableTH className="w-[110px] col-sep px-1.5" sortKey="dest" currentSort={sortBy} onSort={onSortChange!}>Dest</SortableTH>
              <SortableTH className="text-right w-[50px] px-1.5" sortKey="trip" currentSort={sortBy} onSort={onSortChange!}>Trip</SortableTH>
              <SortableTH className="w-[58px] col-sep px-1.5" sortKey="pickup" currentSort={sortBy} onSort={onSortChange!}>Pick Up</SortableTH>
              <SortableTH className="w-[54px] px-1.5" sortKey="eq" currentSort={sortBy} onSort={onSortChange!}>EQ</SortableTH>
              <TH className="text-right w-[44px] px-1.5">Len</TH>
              <TH className="text-center w-[118px] px-1.5">Capacity/Type</TH>
              <TH className="text-center w-[44px] px-1.5">Stops</TH>
              <SortableTH className="text-right w-[54px] col-sep px-1.5" sortKey="weight" currentSort={sortBy} onSort={onSortChange!}>WT</SortableTH>
              <TH className="w-[86px] px-1.5">Commodity</TH>
              <TH className="w-[76px] px-1.5">Dline</TH>
            </TableRow>
          </TableHeader>
        <TableBody>
          {showSkeleton && Array.from({ length: 8 }).map((_, i) => (
            <TableRow key={`skel-${i}`} className={cn(i % 2 === 0 ? "bg-background" : "bg-slate-50")}>
              {Array.from({ length: COL_SPAN }).map((_, j) => (
                <TableCell key={j} className="px-1.5 py-1">
                  <Skeleton className="h-3.5 w-10 rounded" />
                </TableCell>
              ))}
            </TableRow>
          ))}
          {!showSkeleton && sortedLoads.map((load, i) => {
            const age = formatAge(load.posted_at || "");
            const equipLabel = load.equipment_type ? formatEquipmentType(load.equipment_type) : "—";
            const lengthLabel = formatLength(load.length_ft);
            const stopsLabel = formatStopCounts(stopCounts[load.id]);
            const isExpanded = expandedLoadId === load.id;


            return (
              <React.Fragment key={load.id}>
                <TableRow
                  onClick={() => setExpandedLoadId(isExpanded ? null : load.id)}
                  className={cn(
                    "group transition-colors duration-200 cursor-pointer hover:bg-brand-teal/20",
                    i % 2 === 0 ? "bg-background" : "bg-slate-50",
                    load.priority_tag === "priority" && isBiddingOpen(load) && "border-l-[3px] border-l-destructive",
                    isLtlLoad(load.load_type) && "border-l-[4px] border-l-amber-500 bg-amber-50/70 hover:bg-amber-100/80",
                    isExpanded && "bg-brand-teal/10 border-l-2 border-l-brand-teal",
                  )}
                >
                  {/* Age */}
                  <TableCell className="px-1.5 py-1">
                    <div className="flex items-center gap-1">
                      <span className={cn("font-semibold whitespace-nowrap", getAgeText(age.mins))} style={{ fontSize: '0.55rem' }}>{age.text}</span>
                      {load.post_type === "quote" && (
                        <Badge className="text-[9px] px-1.5 py-0 h-4 font-bold bg-amber-500 hover:bg-amber-500 text-white border-0 shrink-0" title="Quote request">
                          QUOTE
                        </Badge>
                      )}
                      {load.visibility_mode === "private_only" && (
                        <PrivateNetworkBadge networkId={load.private_network_id} variant="compact" />
                      )}
                      {isLtlLoad(load.load_type) && <LtlBadge size="compact" />}
                    </div>
                  </TableCell>

                  {/* Origin */}
                  <TableCell className="px-1.5 py-1 whitespace-nowrap col-sep">
                    <span className="text-[12px] font-medium text-foreground">{load.origin_city},</span>
                    <span className="text-[12px] text-gray-600 ml-0.5">{load.origin_state}</span>
                  </TableCell>

                  {/* Dest */}
                  <TableCell className="px-1.5 py-1 whitespace-nowrap col-sep">
                    <div className="flex items-center gap-0.5">
                      <span className="text-muted-foreground/60">→</span>
                      <span className="text-[12px] font-medium text-foreground">{load.destination_city},</span>
                      <span className="text-[12px] text-gray-600">{load.destination_state}</span>
                    </div>
                  </TableCell>

                  {/* Trip */}
                  <TableCell className="text-right px-1.5 py-1">
                    <span className="text-[11px] font-medium text-muted-foreground font-mono">
                      {load.total_miles ? `${Number(load.total_miles).toLocaleString()}mi` : "—"}
                    </span>
                  </TableCell>

                  {/* Pick Up */}
                  <TableCell className="px-1.5 py-1 col-sep">
                    <span className="text-[11px] text-foreground">
                      {format(new Date(load.pickup_date + "T00:00:00"), "MMM d")}
                    </span>
                  </TableCell>

                  {/* EQ */}
                  <TableCell className="text-center px-1.5 py-1 whitespace-nowrap">
                    <span className="text-[11px] font-medium text-foreground/80">{equipLabel}</span>
                  </TableCell>

                  {/* Len */}
                  <TableCell className="text-right px-1.5 py-1">
                    <span className="text-[11px] font-medium text-muted-foreground font-mono">
                      {load.length_ft ? `${load.length_ft}'` : "—"}
                    </span>
                  </TableCell>

                  {/* Capacity/Type */}
                  <TableCell className="text-center px-1.5 py-1">
                    <div className="flex items-center justify-center gap-1">
                      <CapacityBadge loadType={load.load_type} />
                      <NatureChip nature={load.load_nature} />
                    </div>
                  </TableCell>

                  {/* Stops */}
                  <TableCell className="text-center px-1.5 py-1">
                    <span className="text-[10px] font-semibold text-foreground font-mono bg-muted/60 rounded px-1 py-0.5">
                      {stopsLabel}
                    </span>
                  </TableCell>


                  {/* Weight */}
                  <TableCell className="text-right px-1.5 py-1 whitespace-nowrap col-sep">
                    <span className="text-[12px] font-medium text-foreground font-mono">
                      {formatWeight(load.weight_lbs)}
                    </span>
                  </TableCell>

                  {/* Commodity */}
                  <TableCell className="px-1.5 py-1">
                    <span className="text-[11px] text-muted-foreground truncate max-w-[80px] inline-block">
                      {load.commodity || "General"}
                    </span>
                  </TableCell>

                  {/* Bid Deadline */}
                  <TableCell className="px-1.5 py-1">
                    {load.bid_deadline ? (
                      <div className="flex items-center gap-0.5">
                        <Clock className="h-3 w-3 text-amber-500 shrink-0" />
                        <span className="text-[10px] text-foreground">{format(new Date(load.bid_deadline), "MMM d, h:mm a")}</span>
                      </div>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">Open</span>
                    )}
                  </TableCell>

                </TableRow>

                {/* ── Expanded detail row ─────────────────────── */}
                {isExpanded && (
                  <TableRow className="bg-muted/5 border-l-2 border-l-brand-teal hover:bg-muted/5">
                    <TableCell colSpan={COL_SPAN} className="p-0 border-0">
                      <DirectLoadDetailPanel load={load} />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
      </div>
    </div>
  );
}
